import React, { ReactNode, useEffect, useState } from 'react';
import styles from './ProfileMenu.module.scss';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import MenuIcon from '@/assets/Icons/Spotnana-Menu-Hamburger.svg?react';
import AirplaneIcon from '@/assets/Icons/Spotnana-Flight-Fill.svg?react';
import TripsIcon from '@/assets/Icons/Spotnana-Nav-Trips.svg?react';
import ApprovalIcon from '@/assets/Icons/Spotnana-Nav-Approvals.svg?react';
import ReportsIcon from '@/assets/Icons/Spotnana-Reports.svg?react';
import SupportIcon from '@/assets/Icons/Spotnana-Nav-Support.svg?react';
import SignOutIcon from '@/assets/Icons/Spotnana-Logout.svg?react';
import CloseIcon from '@/assets/Icons/Spotnana-Close-Cross.svg?react';
import UserIcon from '@/assets/Icons/Spotnana-User-Traveler-Circle-Fill.svg?react';
import CompanyIcon from '@/assets/Icons/Spotnana-Company.svg?react';
import UsersIcon from '@/assets/Icons/Spotnana-Users-Travelers.svg?react';
import PolicyIcon from '@/assets/Icons/Spotnana-Policy-Document.svg?react';
import CalendarIcon from '@/assets/Icons/Spotnana-Calendar.svg?react';
import TravelerIcon from '@/assets/Icons/Spotnana-Traveler-With-Bag.svg?react';
import AgentIcon from '@/assets/Icons/Spotnana-Agent-Companion-View.svg?react';
import QualityIcon from '@/assets/Icons/Spotnana-Quality-Management-Filled.svg?react';
import InsightsIcon from '@/assets/Icons/Spotnana-Reports-Insights.svg?react';
import StatisticsIcon from '@/assets/Icons/Spotnana-Reports-Statistics.svg?react';
import { useNavigate } from 'react-router';
import routes from '@/constants/routes.ts';
import { Link } from 'react-router-dom';
import breakpoints from '@/constants/breakpoints.ts';
import { useWindowSize } from '@react-hook/window-size';
import classNames from 'classnames';
import { Roles } from '@/api/user.ts';
import { externalLinks } from '@/constants/externalLinks.ts';
import headerMenuPerRole, { HeaderMenu } from '@/constants/headerLinks.tsx';
import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import OutsideClickHandler from 'react-outside-click-handler';

interface MenuItem {
	to: string;
	label: string;
	icon: ReactNode;
}

type UserWithAvatar = App.Data.UserData & {
	avatarUrl?: string | null;
	imageUrl?: string | null;
	photoUrl?: string | null;
	profileImageUrl?: string | null;
};

const travelArrangerGroups: MenuItem[][] = [
	[
		{ to: routes.myClimate, label: 'My Climate', icon: <AirplaneIcon /> },
		{ to: externalLinks.spotnana.book, label: 'Book', icon: <AirplaneIcon /> },
		{ to: externalLinks.spotnana.myTrips, label: 'Trips', icon: <TripsIcon /> },
		{ to: externalLinks.spotnana.myApprovals, label: 'Approvals', icon: <ApprovalIcon /> },
		{ to: externalLinks.spotnana.myReports, label: 'My reports', icon: <ReportsIcon /> },
	],
	[
		{ to: externalLinks.spotnana.myProfile, label: 'Profile', icon: <UserIcon /> },
		{ to: routes.securitySettings, label: 'Security Settings', icon: <UserIcon /> },
		{ to: externalLinks.spotnana.support, label: 'Support', icon: <SupportIcon /> },
	],
];

const tmcAdminGroups: MenuItem[][] = [
	[
		{ to: routes.myClimate, label: 'My Climate', icon: <AirplaneIcon /> },
		{ to: routes.dataCompliancePastTrips, label: 'Climate Pro', icon: <AirplaneIcon /> },
		{ to: externalLinks.spotnana.book, label: 'Book', icon: <AirplaneIcon /> },
		{ to: externalLinks.spotnana.myTrips, label: 'Trips', icon: <TripsIcon /> },
		{ to: externalLinks.spotnana.myApprovals, label: 'Approvals', icon: <ApprovalIcon /> },
		{ to: externalLinks.spotnana.agentTmc, label: 'Agent', icon: <AgentIcon /> },
		{ to: externalLinks.spotnana.travelersSafety, label: 'Traveler safety', icon: <TravelerIcon /> },
		{ to: externalLinks.spotnana.insights, label: 'Insights', icon: <InsightsIcon /> },
		{ to: externalLinks.spotnana.companyReports, label: 'Company reports', icon: <ReportsIcon /> },
		{ to: externalLinks.spotnana.policies, label: 'Policies', icon: <ReportsIcon /> },
	],
	[
		{ to: externalLinks.spotnana.myProfile, label: 'Profile', icon: <UserIcon /> },
		{ to: routes.securitySettings, label: 'Security Settings', icon: <UserIcon /> },
		{ to: externalLinks.spotnana.support, label: 'Support', icon: <SupportIcon /> },
	],
	[
		{ to: externalLinks.spotnana.companySettings, label: 'Company settings', icon: <CompanyIcon /> },
		{ to: externalLinks.spotnana.usersSettings, label: 'People settings', icon: <UsersIcon /> },
	],
];

const tmcAgentGroups: MenuItem[][] = [
	[
		{ to: routes.myClimate, label: 'My Climate', icon: <AirplaneIcon /> },
		{ to: externalLinks.spotnana.book, label: 'Book', icon: <AirplaneIcon /> },
		{ to: externalLinks.spotnana.myTrips, label: 'Trips', icon: <TripsIcon /> },
		{ to: externalLinks.spotnana.myApprovals, label: 'Approvals', icon: <ApprovalIcon /> },
		{ to: externalLinks.spotnana.agentTmc, label: 'Agent', icon: <AgentIcon /> },
		{ to: externalLinks.spotnana.travelersSafety, label: 'Traveller safety', icon: <TravelerIcon /> },
		{ to: externalLinks.spotnana.insights, label: 'Insights', icon: <InsightsIcon /> },
		{ to: externalLinks.spotnana.myReports, label: 'My reports', icon: <ReportsIcon /> },
		{ to: externalLinks.spotnana.companyReports, label: 'Company reports', icon: <ReportsIcon /> },
		{ to: externalLinks.spotnana.policies, label: 'Policies', icon: <ReportsIcon /> },
	],
	[
		{ to: externalLinks.spotnana.myProfile, label: 'Profile', icon: <UserIcon /> },
		{ to: routes.securitySettings, label: 'Security Settings', icon: <UserIcon /> },
		{ to: externalLinks.spotnana.support, label: 'Support', icon: <SupportIcon /> },
	],
	[
		{ to: externalLinks.spotnana.companySettings, label: 'Company settings', icon: <CompanyIcon /> },
		{ to: externalLinks.spotnana.usersSettings, label: 'People settings', icon: <UsersIcon /> },
	],
];

const companyAdminGroups: MenuItem[][] = [
	[
		{ to: routes.myClimate, label: 'My Climate', icon: <AirplaneIcon /> },
		{ to: routes.dataCompliancePastTrips, label: 'Climate Pro', icon: <AirplaneIcon /> },
		{ to: externalLinks.spotnana.book, label: 'Book', icon: <AirplaneIcon /> },
		{ to: externalLinks.spotnana.myTrips, label: 'Trips', icon: <TripsIcon /> },
		{ to: externalLinks.spotnana.myApprovals, label: 'Approvals', icon: <ApprovalIcon /> },
		{ to: externalLinks.spotnana.travelersSafety, label: 'Traveller safety', icon: <TravelerIcon /> },
		{ to: externalLinks.spotnana.insights, label: 'Insights', icon: <InsightsIcon /> },
		{ to: externalLinks.spotnana.companyReports, label: 'Company reports', icon: <ReportsIcon /> },
		{ to: externalLinks.spotnana.policies, label: 'Policies', icon: <ReportsIcon /> },
	],
	[
		{ to: externalLinks.spotnana.myProfile, label: 'Profile', icon: <UserIcon /> },
		{ to: routes.securitySettings, label: 'Security Settings', icon: <UserIcon /> },
		{ to: externalLinks.spotnana.support, label: 'Support', icon: <SupportIcon /> },
	],
	[
		{ to: externalLinks.spotnana.companySettings, label: 'Company settings', icon: <CompanyIcon /> },
		{ to: externalLinks.spotnana.usersSettings, label: 'People settings', icon: <UsersIcon /> },
	],
];

const roleMenuGroups: Partial<Record<App.Enums.SpotnanaRole, MenuItem[][]>> = {
	[Roles.CompanyAdmin]: companyAdminGroups,
	[Roles.TmcAdmin]: tmcAdminGroups,
	[Roles.TmcAgent]: tmcAgentGroups,
	[Roles.TravelArranger]: travelArrangerGroups,
	[Roles.CompanyTravelArranger]: travelArrangerGroups,
};

const profileSettingsGroup: MenuItem[] = [
	{ to: externalLinks.spotnana.myProfile, label: 'Profile', icon: <UserIcon /> },
	{ to: routes.securitySettings, label: 'Security Settings', icon: <UserIcon /> },
	{ to: externalLinks.spotnana.support, label: 'Support', icon: <SupportIcon /> },
];

const getIconForLabel = (label: string) => {
	const normalizedLabel = label.toLowerCase();

	if (normalizedLabel.includes('climate pro')) return <QualityIcon />;
	if (normalizedLabel.includes('my climate') || normalizedLabel.includes('book')) return <AirplaneIcon />;
	if (normalizedLabel.includes('trip')) return <TripsIcon />;
	if (normalizedLabel.includes('approval')) return <ApprovalIcon />;
	if (normalizedLabel.includes('insight')) return <InsightsIcon />;
	if (normalizedLabel.includes('schedule')) return <CalendarIcon />;
	if (normalizedLabel.includes('report') || normalizedLabel.includes('analytics')) return <StatisticsIcon />;
	if (normalizedLabel.includes('traveler') || normalizedLabel.includes('safety')) return <TravelerIcon />;
	if (normalizedLabel.includes('company')) return <CompanyIcon />;
	if (normalizedLabel.includes('tmc')) return <CompanyIcon />;
	if (normalizedLabel.includes('user')) return <UsersIcon />;
	if (normalizedLabel.includes('polic')) return <PolicyIcon />;
	if (normalizedLabel.includes('agent')) return <AgentIcon />;
	if (normalizedLabel.includes('support')) return <SupportIcon />;

	return <ReportsIcon />;
};

const menuItemFromHeaderMenu = (menu: HeaderMenu): MenuItem[] => {
	if (menu.route) {
		return [{ to: menu.route, label: menu.label, icon: getIconForLabel(menu.label) }];
	}

	return (
		menu.submenu?.flatMap(section =>
			section.links.map(link => ({
				to: link.route,
				label: link.label,
				icon: link.icon || getIconForLabel(link.label),
			})),
		) || []
	);
};

const getMenuGroupsForRole = (role?: App.Enums.SpotnanaRole): MenuItem[][] => {
	if (role && roleMenuGroups[role]) return roleMenuGroups[role];

	const headerMenus = role && headerMenuPerRole[role] ? headerMenuPerRole[role] : headerMenuPerRole.default;
	const primaryGroup = headerMenus
		.filter(menu => menu.label !== 'Support')
		.flatMap(menuItemFromHeaderMenu)
		.filter(item => item.to !== externalLinks.spotnana.support);

	return [primaryGroup, profileSettingsGroup];
};

const getUserAvatarUrl = (user?: App.Data.UserData | null): string | undefined => {
	const userWithAvatar = user as UserWithAvatar | undefined;

	return (
		userWithAvatar?.avatarUrl ||
		userWithAvatar?.profileImageUrl ||
		userWithAvatar?.photoUrl ||
		userWithAvatar?.imageUrl ||
		undefined
	);
};

const ProfileMenu: React.FC = () => {
	const [width] = useWindowSize();
	const user = useWhoamiStore(state => state.user);
	const navigate = useNavigate();
	const [isPopupShown, setIsPopupShown] = useState<boolean>(false);
	const isDesktop = width > breakpoints.desktop;
	const isFullScreenMenu = width <= breakpoints.phoneWide;

	const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`;
	const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
	const avatarUrl = getUserAvatarUrl(user);
	const menuGroups = getMenuGroupsForRole(user?.spotnanaRole || undefined);

	const { refs, floatingStyles } = useFloating({
		placement: 'bottom-end',
		middleware: [offset({ mainAxis: 8, crossAxis: 0 }), flip(), shift({ padding: 16 })],
		whileElementsMounted: autoUpdate,
	});

	useEffect(() => {
		if (isDesktop || !isPopupShown || !isFullScreenMenu) return;

		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, [isDesktop, isFullScreenMenu, isPopupShown]);

	const closeMenu = () => setIsPopupShown(false);
	const signOut = () => {
		closeMenu();
		navigate(routes.logout);
	};

	const renderIdentity = (variant: 'dropdown' | 'modal') => (
		<div
			className={classNames(styles.identity, {
				[styles.identityDropdown]: variant === 'dropdown',
				[styles.identityModal]: variant === 'modal',
			})}
		>
			<div className={styles.largeAvatar}>
				{avatarUrl ? (
					<img className={styles.avatarImage} src={avatarUrl} alt={fullName || 'User avatar'} />
				) : (
					<span className={styles.avatarInitials}>{initials}</span>
				)}
				{!avatarUrl && <span className={styles.avatarBadge} aria-hidden="true" />}
			</div>
			<p className={styles.userName}>{fullName}</p>
			{user?.email && <p className={styles.userEmail}>{user.email}</p>}
		</div>
	);

	const renderMenuGroup = (items: MenuItem[], groupKey?: string | number) => (
		<div className={styles.menuGroup} key={groupKey}>
			{items.map(item => (
				<Link key={item.label} to={item.to} className={styles.menuItem} onClick={closeMenu}>
					<span className={styles.menuIcon}>{item.icon}</span>
					<span className={styles.menuText}>{item.label}</span>
				</Link>
			))}
		</div>
	);

	const renderSignOut = () => (
		<div className={styles.menuGroup}>
			<button type="button" className={classNames(styles.menuItem, styles.signOutItem)} onClick={signOut}>
				<span className={styles.menuIcon}>
					<SignOutIcon />
				</span>
				<span className={styles.menuText}>Sign out</span>
			</button>
		</div>
	);

	const renderDropdown = () => (
		<div className={styles.dropdown}>
			<div className={styles.dropdownContent}>
				{renderIdentity('dropdown')}
				{renderMenuGroup(menuGroups[1].slice(0, 2))}
				{renderSignOut()}
			</div>
		</div>
	);

	const renderResponsiveDialog = () => (
		<FloatingPortal>
			<div
				className={styles.modalOverlay}
				role="presentation"
				style={{ zIndex: 'var(--z-index-overlay)' }}
				onMouseDown={closeMenu}
			>
				<div
					className={styles.modalPanel}
					role="dialog"
					aria-modal="true"
					aria-label="Profile and navigation menu"
					onMouseDown={event => event.stopPropagation()}
				>
					<button className={styles.closeButton} type="button" aria-label="Close" onClick={closeMenu}>
						<CloseIcon />
					</button>
					<div className={styles.modalPanelContent}>
						{renderIdentity('modal')}
						{menuGroups.map((group, index) => renderMenuGroup(group, index))}
						{renderSignOut()}
					</div>
				</div>
			</div>
		</FloatingPortal>
	);

	return (
		<div className={styles.profileMenuWrapper}>
			{isDesktop ? (
				<>
					<div className={styles.initialsWrapper} ref={refs.setReference}>
						<button
							className={styles.initials}
							type="button"
							onClick={() => setIsPopupShown(prev => !prev)}
							aria-label="Profile and settings"
							aria-expanded={isPopupShown}
						>
							{avatarUrl ? (
								<img className={styles.avatarImage} src={avatarUrl} alt={fullName || 'User avatar'} />
							) : (
								<span className={styles.avatarInitials}>{initials}</span>
							)}
						</button>
					</div>
					{isPopupShown && (
						<FloatingPortal>
							<OutsideClickHandler onOutsideClick={closeMenu}>
								<div
									ref={refs.setFloating}
									style={{ ...floatingStyles, zIndex: 'var(--z-index-overlay)' }}
								>
									{renderDropdown()}
								</div>
							</OutsideClickHandler>
						</FloatingPortal>
					)}
				</>
			) : (
				<>
					<button
						className={styles.hamburgerMenu}
						type="button"
						aria-label="Navigation menu"
						aria-expanded={isPopupShown}
						onClick={() => setIsPopupShown(prev => !prev)}
					>
						<MenuIcon aria-hidden="true" />
						<span className={styles.compactAvatar}>
							{avatarUrl ? (
								<img className={styles.avatarImage} src={avatarUrl} alt={fullName || 'User avatar'} />
							) : (
								<span className={styles.avatarInitials}>{initials}</span>
							)}
						</span>
					</button>
					{isPopupShown && renderResponsiveDialog()}
				</>
			)}
		</div>
	);
};

export default ProfileMenu;
